import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useHistory } from 'react-router-dom';

import { createWidget } from '../service/widget.service';
import PFDialogContainer from '../../../shared/PFDialog/PFDialogContainer';
import {
  getDashboardById,
  upDateDashboard,
} from '../../Dashboard/services/Dashboard.services';
import {
  CloneWidgetProps,
  DashboardResponse,
} from '../Interface/clone-widget.interface';

const CloneWidget = ({
  cloneWidgetDialog,
  setCloneWidgetDialog,
  hasMatchingPrimaryKeyWithAddField,
}: CloneWidgetProps) => {
  const [loading, setSaveLoading] = useState(false);
  const [widgetQuery, setWidgetQuery] = useState<DashboardResponse | null>(
    null
  );

  const toast = useRef<Toast>(null);
  const navigateAfterToastRef = useRef(false);

  const history = useHistory();

  const dashboardId = cloneWidgetDialog?.dashboardId;
  const initialValues = cloneWidgetDialog?.initialValues;

  const widgetsFormInitialValues = {
    ...(initialValues || {}),
    widget_name: initialValues?.widget_name
      ? `${initialValues.widget_name} Copy`
      : '',
  };

  const getDashboardDetails = async (id: number) => {
    try {
      setWidgetQuery(null);

      const response = await getDashboardById(id);

      if (response?.error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response?.message || 'Failed to fetch dashboard details',
          life: 3000,
        });

        return;
      }

      setWidgetQuery(response?.data ?? null);
    } catch (error) {
      console.error('getDashboardDetails error:', error);

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Something went wrong while fetching dashboard details',
        life: 3000,
      });
    }
  };

  const updateHomeDashboard = async (
    id: number,
    widgetTemplateIds: number[]
  ): Promise<boolean> => {
    try {
      const response = await upDateDashboard(id, widgetTemplateIds);

      if (response?.error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response?.message || 'Failed to update dashboard',
          life: 3000,
        });

        return false;
      }

      return true;
    } catch (error) {
      console.error('updateHomeDashboard error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Something went wrong while updating the dashboard',
        life: 3000,
      });
      return false;
    }
  };

  const getWidgetTemplateIds = (): number[] => {
    return (
      widgetQuery?.widgets
        ?.map(
          widget =>
            widget?.widget_template?.widget_template_id ??
            widget?.widget_template_id
        )
        .filter((id): id is number => id !== undefined && id !== null) ?? []
    );
  };

  const handleAssignWidget = async (widgetId: number): Promise<boolean> => {
    if (!dashboardId) {
      return true;
    }

    try {
      const widgetTemplateIds = getWidgetTemplateIds();

      if (!widgetTemplateIds.includes(widgetId)) {
        widgetTemplateIds.push(widgetId);
      }

      return await updateHomeDashboard(dashboardId, widgetTemplateIds);
    } catch (error) {
      console.error('handleAssignWidget error:', error);

      return false;
    }
  };

  const widgetFormik = useFormik({
    initialValues: widgetsFormInitialValues,

    enableReinitialize: true,

    onSubmit: async formValues => {
      if (loading) {
        return;
      }

      setSaveLoading(true);

      try {
        let values = {
          ...formValues,
        };

        if (values.is_third_party === 1) {
          values = {
            ...values,
            report_group_by: null,
            report_group_by_aggr: null,
            chart_configuration: {
              ...values.chart_configuration,
              groupBy: [],
            },
          };
        }

        const hasAdditionalDimensions = hasMatchingPrimaryKeyWithAddField(
          values.table_configuration
        );

        if (!hasAdditionalDimensions) {
          values = {
            ...values,
            custom_properties: {
              ...values.custom_properties,
              additional_dimensions: [],
            },
          };
        }

        const response = await createWidget(values);

        if (!response?.status) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create widget',
            life: 3000,
          });

          return;
        }

        const widgetId = response?.data?.[0]?.WidgetTemplateId;

        if (!widgetId) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Widget was created but its ID could not be found.',
            life: 3000,
          });

          return;
        }
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Widget copied successfully.',
          life: 3000,
        });

        const isAssigned = await handleAssignWidget(widgetId);

        if (isAssigned) {
          closeDialog();
          history.push('/dashboard');
        }

        navigateAfterToastRef.current = true;
      } catch (error) {
        console.error('Clone widget error:', error);

        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong while cloning the widget.',
          life: 3000,
        });
      } finally {
        setSaveLoading(false);
      }
    },
  });

  const closeDialog = () => {
    setCloneWidgetDialog({
      isVisible: false,
      initialValues: null,
      dashboardId: null,
    });

    setWidgetQuery(null);
  };

  useEffect(() => {
    if (!cloneWidgetDialog?.isVisible || !dashboardId) {
      return;
    }

    getDashboardDetails(dashboardId);
  }, [cloneWidgetDialog?.isVisible, dashboardId]);

  const isSubmitDisabled =
    !widgetQuery || loading || !widgetFormik.values.widget_name?.trim();

  return (
    <>
      <PFDialogContainer
        draggable={false}
        visible={cloneWidgetDialog?.isVisible}
        onHide={closeDialog}
        header="Save Widget As"
        className="w-10 md:w-6 lg:w-5"
        footer={
          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              label="Close"
              size="small"
              outlined
              type="button"
              onClick={closeDialog}
              disabled={loading}
            />

            <Button
              label="Submit"
              size="small"
              type="submit"
              form="clone-widget-form"
              className="height-40"
              loading={loading}
              disabled={isSubmitDisabled}
            />
          </div>
        }
        pt={{
          headerIcons: {
            className: 'hidden',
          },
          footer: {
            className: 'justify-content-end',
          },
        }}
      >
        <form id="clone-widget-form" onSubmit={widgetFormik.handleSubmit}>
          <div>
            <span className="p-float-label mt-1">
              <InputText
                id="widget_name"
                name="widget_name"
                autoFocus
                type="text"
                className="w-full"
                value={widgetFormik.values.widget_name || ''}
                onChange={widgetFormik.handleChange}
                disabled={loading}
              />

              <label htmlFor="widget_name" className="block mb-2">
                Widget Name
              </label>
            </span>
          </div>
        </form>
      </PFDialogContainer>

      <Toast ref={toast} />
    </>
  );
};

export default CloneWidget;
